import { useState, useCallback, useRef } from "react";
import type { WBSTreeNode, WBSActivity } from "../../../models";
import {
  fetchWBSRoots,
  fetchWBSChildren,
  fetchWBSActivities,
  fetchWBSFullTree,
} from "../../../api/clientV2";

interface TreeState {
  roots: WBSTreeNode[];
  childrenMap: Map<number, WBSTreeNode[]>;
  activitiesMap: Map<number, WBSActivity[]>;
  expandedSet: Set<number>;
  loadingSet: Set<number>;
}

export function useWBSTree(scheduleId: number) {
  const [state, setState] = useState<TreeState>({
    roots: [],
    childrenMap: new Map(),
    activitiesMap: new Map(),
    expandedSet: new Set(),
    loadingSet: new Set(),
  });
  const [rootsLoading, setRootsLoading] = useState(false);
  const [expandAllLoading, setExpandAllLoading] = useState(false);

  // Ref to avoid stale closures in toggleNode
  const stateRef = useRef(state);
  stateRef.current = state;

  const loadRoots = useCallback(async () => {
    setRootsLoading(true);
    try {
      const roots = await fetchWBSRoots(scheduleId);
      setState((prev) => ({ ...prev, roots }));
    } finally {
      setRootsLoading(false);
    }
  }, [scheduleId]);

  const toggleNode = useCallback(
    async (node: WBSTreeNode) => {
      const nodeId = node.id;
      const current = stateRef.current;

      // Collapse if already expanded
      if (current.expandedSet.has(nodeId)) {
        setState((prev) => {
          const next = new Set(prev.expandedSet);
          next.delete(nodeId);
          return { ...prev, expandedSet: next };
        });
        return;
      }

      // A node can have BOTH child WBS nodes and its own directly-attached
      // activities — fetch whichever is present and not already cached.
      const needChildren =
        node.child_count > 0 && !current.childrenMap.has(nodeId);
      const needActivities =
        node.direct_activity_count > 0 && !current.activitiesMap.has(nodeId);

      // Nothing to load (fully cached, or a node with neither) → just expand.
      if (!needChildren && !needActivities) {
        setState((prev) => {
          const next = new Set(prev.expandedSet);
          next.add(nodeId);
          return { ...prev, expandedSet: next };
        });
        return;
      }

      // Set loading
      setState((prev) => {
        const next = new Set(prev.loadingSet);
        next.add(nodeId);
        return { ...prev, loadingSet: next };
      });

      try {
        const [children, activities] = await Promise.all([
          needChildren ? fetchWBSChildren(nodeId) : Promise.resolve(null),
          needActivities ? fetchWBSActivities(nodeId) : Promise.resolve(null),
        ]);
        setState((prev) => {
          const nextChildren = new Map(prev.childrenMap);
          if (children !== null) nextChildren.set(nodeId, children);
          const nextActivities = new Map(prev.activitiesMap);
          if (activities !== null) nextActivities.set(nodeId, activities);
          const nextExpanded = new Set(prev.expandedSet);
          nextExpanded.add(nodeId);
          const nextLoading = new Set(prev.loadingSet);
          nextLoading.delete(nodeId);
          return {
            ...prev,
            childrenMap: nextChildren,
            activitiesMap: nextActivities,
            expandedSet: nextExpanded,
            loadingSet: nextLoading,
          };
        });
      } catch {
        setState((prev) => {
          const nextLoading = new Set(prev.loadingSet);
          nextLoading.delete(nodeId);
          return { ...prev, loadingSet: nextLoading };
        });
      }
    },
    [] // stateRef handles freshness
  );

  const expandAll = useCallback(async () => {
    setExpandAllLoading(true);
    try {
      const allNodes = await fetchWBSFullTree(scheduleId);

      // Group by parent_id to build children map
      const newMap = new Map<number, WBSTreeNode[]>();
      for (const node of allNodes) {
        if (node.parent_id == null) continue;
        const siblings = newMap.get(node.parent_id) || [];
        siblings.push(node);
        newMap.set(node.parent_id, siblings);
      }

      // Expand every node that has WBS children
      const newExpanded = new Set<number>();
      newMap.forEach((_children, parentId) => {
        newExpanded.add(parentId);
      });

      setState((prev) => ({
        ...prev,
        childrenMap: newMap,
        expandedSet: newExpanded,
        // Note: activitiesMap not populated — leaf nodes still lazy-load activities on click
      }));
    } finally {
      setExpandAllLoading(false);
    }
  }, [scheduleId]);

  const collapseAll = useCallback(() => {
    setState((prev) => ({
      ...prev,
      expandedSet: new Set(),
      // Keep caches for instant re-expansion
    }));
  }, []);

  const getChildren = useCallback(
    (nodeId: number): WBSTreeNode[] => {
      return state.childrenMap.get(nodeId) || [];
    },
    [state.childrenMap]
  );

  const getActivities = useCallback(
    (nodeId: number): WBSActivity[] => {
      return state.activitiesMap.get(nodeId) || [];
    },
    [state.activitiesMap]
  );

  const isExpanded = useCallback(
    (nodeId: number): boolean => {
      return state.expandedSet.has(nodeId);
    },
    [state.expandedSet]
  );

  const isLoading = useCallback(
    (nodeId: number): boolean => {
      return state.loadingSet.has(nodeId);
    },
    [state.loadingSet]
  );

  return {
    roots: state.roots,
    rootsLoading,
    expandAllLoading,
    loadRoots,
    toggleNode,
    expandAll,
    collapseAll,
    getChildren,
    getActivities,
    isExpanded,
    isLoading,
  };
}
